import React, { useState, useEffect } from 'react';
import { X, Phone, ClipboardList, Maximize2, Minimize2, Download } from 'lucide-react';

const SCRIPT_IMG = '/prospection_guide/script_appel.jpeg';
const FICHE_IMG  = '/prospection_guide/fiche_prospection.jpeg';

/**
 * Modale plein écran — Guide commercial Prospections.
 * Deux onglets : Script d'appel • Fiche & Objections (recto/verso).
 */
export default function ProspectionGuideModal({ open, onClose, defaultTab = 'script' }) {
  const [tab, setTab] = useState(defaultTab);
  useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const img = tab === 'script' ? SCRIPT_IMG : FICHE_IMG;
  const label = tab === 'script' ? 'Script d\'appel Collectivités' : 'Fiche de prospection & objections';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col" data-testid="prospection-guide-modal">
      {/* Header */}
      <div className="bg-[#1C49A6] text-white px-5 py-3 flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          <h3 className="font-bold text-sm uppercase tracking-widest">Guide commercial Reno Resolve</h3>
        </div>
        <div className="flex items-center gap-1 ml-4 bg-white/10 rounded-lg p-1">
          <TabButton active={tab === 'script'} onClick={() => setTab('script')} icon={Phone} label="Script d'appel" testid="guide-tab-script" />
          <TabButton active={tab === 'fiche'}  onClick={() => setTab('fiche')}  icon={ClipboardList} label="Fiche & objections" testid="guide-tab-fiche" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <a href={img} download className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white px-2 py-1 rounded-md hover:bg-white/10">
            <Download className="w-3.5 h-3.5" /> Télécharger
          </a>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10" aria-label="Fermer" data-testid="guide-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Image */}
      <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
        <img
          src={img}
          alt={label}
          className="max-w-full h-auto shadow-2xl rounded-md bg-white"
          style={{ maxHeight: 'none' }}
          data-testid={`guide-image-${tab}`}
        />
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition-colors ${
        active ? 'bg-white text-[#1C49A6]' : 'text-white/80 hover:text-white hover:bg-white/10'
      }`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}


/**
 * Bouton flottant en bas à droite — accessible dans la fiche prospect
 * pour ouvrir le Script d'appel pendant l'appel commercial.
 */
export function FloatingScriptButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[#1C49A6] hover:bg-[#163C89] text-white shadow-xl hover:shadow-2xl transition-all"
      data-testid="floating-script-btn"
    >
      <div className="relative">
        <Phone className="w-4 h-4" />
        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
      </div>
      <span className="text-sm font-semibold">Script d'appel</span>
    </button>
  );
}
